import React from "react";
import {
  Shield,
  Mail,
  Globe,
  Calendar,
  Users,
  Lock,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
} from "lucide-react";

const PrivacyPolicy = () => {
  const Section = ({ icon: Icon, title, children, className = "" }) => (
    <section className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${className}`}>
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </section>
  );

  const InfoBox = ({ children, type = "default" }) => {
    const styles = {
      default: "bg-gray-50 border-l-4 border-gray-400",
      success: "bg-green-50 border-l-4 border-green-400",
      warning: "bg-yellow-50 border-l-4 border-yellow-400",
      danger: "bg-red-50 border-l-4 border-red-400",
      info: "bg-blue-50 border-l-4 border-blue-400",
    };

    return <div className={`${styles[type]} p-4 rounded-r-lg`}>{children}</div>;
  };

  const ListItem = ({ children, color = "blue" }) => (
    <div className={`flex items-center gap-3 p-3 bg-${color}-50 rounded-lg`}>
      <div className={`w-2 h-2 bg-${color}-500 rounded-full`}></div>
      <span className="text-gray-700">{children}</span>
    </div>
  );

  const collectInfo = [
    "Name",
    "Email address",
    "Phone number",
    "Company name",
    "Job title",
    "Location (Country/City)",
    "IP address",
    "Device information",
  ];

  const howWeCollect = [
    "When you submit forms on our website or LinkedIn Lead Gen Forms.",
    "When you contact us via email, chat, or phone.",
    "When you engage with our marketing campaigns, advertisements, or social media.",
    "Automatically via cookies and tracking technologies (Google Analytics, LinkedIn Insights, Meta Pixel, etc.).",
  ];

  const useInfo = [
    "Contact you regarding our services.",
    "Send you marketing communications (if you have consented).",
    "Personalize our advertisements and content.",
    "Provide customer support.",
    "Improve our services, website, and user experience.",
    "Comply with legal obligations.",
  ];

  const shareInfo = [
    "Trusted service providers that assist us in marketing, analytics, payment processing, hosting, or communications.",
    "LinkedIn, Meta, Google, and other platforms for ad targeting, personalization, and measurement.",
    "Legal authorities if required by law.",
  ];

  const rights = [
    { icon: Eye, text: "Access your personal information" },
    { icon: Edit, text: "Correct or update inaccurate information" },
    { icon: Trash2, text: "Withdraw consent for marketing at any time" },
    { icon: Trash2, text: "Request deletion of your personal data" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Privacy Policy
              </h1>
              <p className="text-gray-600 mt-1">Aruba Creative</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Effective Date: 01/05/2025</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          <Section
            icon={Users}
            title="Introduction"
            className="bg-green-100 text-green-600"
          >
            <InfoBox type="success">
              <p className="text-gray-700">
                At Aruba Creative ("we," "our," "us"), your privacy is very
                important to us. This Privacy Policy explains how we collect,
                use, disclose, and protect your personal information when you
                visit our website, use our services, or interact with our
                advertisements and lead generation forms, including LinkedIn
                Ads.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Eye}
            title="Information We Collect"
            className="bg-blue-100 text-blue-600"
          >
            <p className="text-gray-700 mb-4">
              We may collect the following types of personal information:
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              {collectInfo.map((item, index) => (
                <ListItem key={index} color="blue">
                  {item}
                </ListItem>
              ))}
            </div>
            <InfoBox>
              <p className="text-gray-700">
                <strong>Additional:</strong> Any information you voluntarily
                submit through forms, emails, or messages
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Globe}
            title="How We Collect Information"
            className="bg-purple-100 text-purple-600"
          >
            <div className="space-y-3">
              {howWeCollect.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg"
                >
                  <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section
            icon={Edit}
            title="Use of Information"
            className="bg-orange-100 text-orange-600"
          >
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <div className="space-y-2">
              {useInfo.map((item, index) => (
                <ListItem key={index} color="orange">
                  {item}
                </ListItem>
              ))}
            </div>
          </Section>

          <Section
            icon={Users}
            title="How We Share Information"
            className="bg-red-100 text-red-600"
          >
            <InfoBox type="danger">
              <p className="text-red-800 font-semibold">
                We do not sell or rent your personal information.
              </p>
            </InfoBox>
            <p className="text-gray-700 my-4">We may share your data with:</p>
            <div className="space-y-2">
              {shareInfo.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section
            icon={Calendar}
            title="Data Retention"
            className="bg-indigo-100 text-indigo-600"
          >
            <InfoBox type="info">
              <p className="text-gray-700">
                We retain your personal data only for as long as necessary to
                fulfill the purposes for which it was collected, or as required
                by law.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Shield}
            title="Your Rights"
            className="bg-green-100 text-green-600"
          >
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              {rights.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-lg"
                >
                  <item.icon className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
            <InfoBox type="warning">
              <p className="text-gray-700 mb-2">
                <strong>To exercise these rights, contact us at:</strong>
              </p>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <a
                  href="mailto:info@aruba-creative.com"
                  className="text-blue-600 hover:underline"
                >
                  info@aruba-creative.com
                </a>
              </div>
            </InfoBox>
          </Section>

          <Section
            icon={Lock}
            title="Data Security"
            className="bg-red-100 text-red-600"
          >
            <InfoBox type="danger">
              <p className="text-gray-700">
                We take reasonable technical, administrative, and physical
                security measures to protect your information from unauthorized
                access, disclosure, or misuse.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Globe}
            title="Third-Party Links"
            className="bg-yellow-100 text-yellow-600"
          >
            <InfoBox type="warning">
              <p className="text-gray-700">
                Our website may contain links to external websites. We are not
                responsible for their privacy practices. Please review their
                privacy policies independently.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={AlertCircle}
            title="LinkedIn Lead Generation Disclaimer"
            className="bg-blue-100 text-blue-600"
          >
            <InfoBox type="info">
              <p className="text-gray-700">
                When you submit your data via LinkedIn Lead Gen Forms, your
                information is shared both with LinkedIn and Aruba Creative.
                LinkedIn may also use this information according to its own
                privacy policy.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Edit}
            title="Changes to This Policy"
            className="bg-purple-100 text-purple-600"
          >
            <InfoBox>
              <p className="text-gray-700">
                We may update this Privacy Policy periodically. Any changes will
                be posted on this page with an updated effective date.
              </p>
            </InfoBox>
          </Section>

          <Section
            icon={Mail}
            title="Contact Us"
            className="bg-green-100 text-green-600"
          >
            <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                If you have questions or concerns, please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">
                    Aruba Creative
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <a
                    href="mailto:info@aruba-creative.com"
                    className="text-blue-600 hover:underline"
                  >
                    info@aruba-creative.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <a
                    href="https://aruba-creative.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://aruba-creative.com
                  </a>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
